/**
 * <biosphere-cart-drawer> — cart-specific behaviour around the dialog
 * primitive. Behaviour: reference/specs/cart.md (Drawer).
 *
 * Keeps the trigger's item count fresh from cart-change events and opens
 * the drawer when an add succeeds. Opening goes through the dialog's own
 * trigger click so its wiring (open event, scroll lock) runs unchanged.
 */
class BiosphereCartDrawer extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(
      "biosphere:cart:change",
      (event) => this.#onCartChange(event),
      { signal },
    );
    document.addEventListener("biosphere:cart:adding", () => this.#onAdding(), {
      signal,
    });
  }

  /**
   * An add has started. Open now and mark the surface busy, so the click is answered
   * immediately and the wait happens where the result will appear. biosphere-cart-items
   * clears the busy state when the fresh cart lands, so a failed add releases it too.
   */
  #onAdding() {
    const items = this.querySelector("biosphere-cart-items");
    if (items) items.setAttribute("aria-busy", "true");
    // Marks the heading's count stale for the same window, so the drawer does not
    // open onto "0 items" beside a cart that is taking its first line. Cleared by
    // the cart-change handler below, whichever way the add ends.
    this.setAttribute("data-adding", "");
    this.#open();
  }

  #open() {
    const dialog = this.querySelector("dialog");
    if (!dialog || dialog.open) return;
    this.querySelector("[data-trigger]")?.click();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onCartChange(event) {
    const { cart, action } = event.detail ?? {};

    this.removeAttribute("data-adding");

    const count = cart?.item_count;
    if (typeof count === "number") {
      // The trigger shows the bare number; the drawer heading shows the
      // pluralised label, whose two forms Liquid pre-translated onto the
      // element. JS only substitutes the marker.
      for (const display of this.querySelectorAll(
        "[data-cart-count-display]",
      )) {
        display.textContent = String(count);
      }
      for (const label of this.querySelectorAll("[data-cart-count-label]")) {
        const template =
          count === 1 ? label.dataset.countOne : label.dataset.countOther;
        if (template) label.textContent = template.replace("[count]", count);
      }
    }

    // Still opened from here as well: an add published by something that
    // does not announce its start (a quick-add elsewhere, a future
    // surface) has to open the drawer too.
    if (action === "add") this.#open();
  }
}

if (!customElements.get("biosphere-cart-drawer")) {
  customElements.define("biosphere-cart-drawer", BiosphereCartDrawer);
}
